import useTranslation from 'next-translate/useTranslation';

// Components
import Icon from '@/components/base/Icon';

export enum EmptyStateType {
    NoContent = 'NoContent',
    Error = 'Error',
    Empty = 'Empty',
    Loading = 'Loading',
    NoResults = 'NoResults'
}

export interface EmptyStateProps {
    /**
     * The type of empty state to display
     */
    type?: EmptyStateType;
    /**
     * Custom icon to override the default one
     */
    icon?: string;
    /**
     * Custom title to override the default one
     */
    title?: string;
    /**
     * Custom description to override the default one
     */
    description?: string;
    /**
     * Additional CSS classes to apply to the component
     */
    className?: string;
    /**
     * The size of the empty state
     * 'sm': smaller size with 16px icon and 12px text
     * 'md': default size with larger icon and text
     */
    size?: 'sm' | 'md';
}

export const EmptyState = ({
    type,
    icon,
    title,
    description,
    className = '',
    size = 'md'
}: EmptyStateProps) => {
    const { t } = useTranslation('commons');

    const defaultEmptyStateMeta = {
        NoContent: {
            icon: 'document',
            title: t('empty_state.no_content_title'),
            description: t('empty_state.no_content_description')
        },
        Error: {
            icon: 'alert',
            title: t('empty_state.error_title'),
            description: t('empty_state.error_description')
        },
        Empty: {
            icon: 'info',
            title: t('empty_state.empty_title'),
            description: t('empty_state.empty_description')
        },
        Loading: {
            icon: 'refresh',
            title: t('empty_state.loading_title'),
            description: t('empty_state.loading_description')
        },
        NoResults: {
            icon: 'search',
            title: t('empty_state.no_results_title'),
            description: t('empty_state.no_results_description')
        }
    };

    const emptyStateMeta = defaultEmptyStateMeta[type];

    const iconSizeClass = size === 'sm' ? 'text-base' : 'text-4xl';
    const titleSizeClass = size === 'sm' ? 'text-xs' : 'text-lg';
    const descriptionSizeClass = size === 'sm' ? 'text-xs' : 'text-base';
    const spacingClass = size === 'sm' ? 'space-y-2' : 'space-y-5';

    return (
        <div className={`flex flex-col items-center justify-center ${spacingClass} ${className}`}>
            <Icon
                icon={icon || emptyStateMeta?.icon}
                className={`${iconSizeClass} text-icoGray-500 dark:text-icoDarkMode-wolf`}
            />
            <div className="flex flex-col items-center text-center">
                <span className={`font-GSemiBold ${titleSizeClass} text-icoBlack dark:text-white`}>
                    {title || emptyStateMeta?.title}
                </span>
                <span
                    className={`font-GMedium ${descriptionSizeClass} text-icoGray-500 dark:text-icoDarkMode-wolf`}>
                    {description || emptyStateMeta?.description}
                </span>
            </div>
        </div>
    );
};
